import { useTranslation } from 'react-i18next';

import { Box, TextField } from '@mui/material';
import { SCUIButton, SCUINoRecordComponent } from '../../../../atomic/atoms';
import { SCUICustomDataGrid } from '../../../../atomic/molecules';

import airdropShipment from '../../../../assets/icons/svg/pageIcons/airdropShipment.svg';
import TimeHelper from '../../../../utils/time';

import { SDS } from '../summaryDetailSearch.classes';

interface SummaryDetailSearchListProps {
    rows: any[];
    orderNumber: string;
    setOrderNumber: (value: string) => void;
    handleSearch: () => void;
    isSearched: boolean;
}

const formatAmount = (value: number | null | undefined) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return '-';
    }
    return Number(value).toLocaleString('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

export const SummaryDetailSearchList = ({
    rows,
    orderNumber,
    setOrderNumber,
    handleSearch,
    isSearched,
}: SummaryDetailSearchListProps) => {
    const { t } = useTranslation();

    const columns = [
        {
            field: 'orderNumber',
            headerName: t('summaryAggregation.summaryDetailSearch.columns.orderNumber'),
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (values: any) => values?.row?.orderNumber || '-',
        },
        {
            field: 'orderDate',
            headerName: t('summaryAggregation.summaryDetailSearch.columns.orderDate'),
            flex: 1.2,
            align: 'center',
            headerAlign: 'center',
            renderCell: (values: any) =>
                values?.row?.orderDate ? TimeHelper.formatDateHourSecond(values.row.orderDate) : '-',
        },
        {
            field: 'summaryDate',
            headerName: t('summaryAggregation.summaryDetailSearch.columns.summaryDate'),
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (values: any) =>
                values?.row?.summaryDate ? TimeHelper.formatDateWithDot(values.row.summaryDate) : '-',
        },
        {
            field: 'description',
            headerName: t('summaryAggregation.summaryDetailSearch.columns.description'),
            flex: 1.5,
            align: 'left',
            headerAlign: 'center',
            renderCell: (values: any) => {
                const description = values?.row?.itemDefinitionDescription;
                return typeof description === 'string' ? description.trim() : '-';
            },
        },
        {
            field: 'action',
            headerName: t('summaryAggregation.summaryDetailSearch.columns.action'),
            flex: 1.2,
            align: 'left',
            headerAlign: 'center',
            renderCell: (values: any) => values?.row?.action || '-',
        },
        {
            field: 'amount',
            headerName: t('summaryAggregation.summaryDetailSearch.columns.amount'),
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (values: any) => formatAmount(values?.row?.amount),
        },
    ];

    return (
        <Box className={SDS.list}>
            <Box
                className={SDS.listSearchBar}
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    margin: '24px 0 32px 0',
                    gap: 2,
                    '& .sc-btn__text': {
                        padding: '8px 16px !important',
                    },
                }}
            >
                <p
                    className={SDS.listSearchLabel}
                    style={{ fontSize: '14px', fontWeight: '600', margin: 0 }}
                >
                    {t('summaryAggregation.summaryDetailSearch.orderNumber')}:
                </p>
                <TextField
                    className={SDS.orderNumberInput}
                    size="small"
                    value={orderNumber}
                    onChange={(e) => setOrderNumber(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && orderNumber.trim()) {
                            handleSearch();
                        }
                    }}
                    sx={{ minWidth: 220 }}
                />
                <SCUIButton
                    className={SDS.btnSearch}
                    text={t('general.search')}
                    type="primary"
                    sizeType="fixed"
                    size={'m'}
                    disabled={!orderNumber.trim()}
                    onClick={handleSearch}
                />
            </Box>
            <Box className={SDS.listGridContainer}>
                {!isSearched ? (
                    <Box className={SDS.listNoRecord}>
                        <SCUINoRecordComponent img={airdropShipment} />
                    </Box>
                ) : (
                    <Box className={SDS.listGrid}>
                        <SCUICustomDataGrid
                            getRowClassName={'sa-summary-detail-search-grid'}
                            rows={rows || []}
                            columns={columns}
                            rowHeight={75}
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
};
